import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';

import { API_ENDPOINT } from '../constants';
import { fetchWrapper } from '../helpers';

import type { ActionReducerMapBuilder, PayloadAction } from '@reduxjs/toolkit';
import type { IPayor, IServiceCodeDescription } from '../utils/types';

const name = 'payors';
const initialState = createInitialState();
const reducers = createReducers();
// SAFETY: Legacy thunk action creators use the store dispatch result contract.
const extraActions = createExtraActions() as any;
const slice = createSlice({
  name,
  initialState,
  reducers,
  extraReducers: (builder: ActionReducerMapBuilder<PayorState>) => createExtraReducers(builder),
});

export interface PayorState {
  current: IPayor | null;
  list: Array<IPayor>;
  error: any;
  loading: boolean;
}

function createInitialState(): PayorState {
  return {
    current: null,
    list: [],
    loading: false,
    error: null,
  };
}

function createReducers() {
  return {};
}

function createExtraActions() {
  const baseUrl = `${API_ENDPOINT}/payors`;

  return {
    getList: getList(),
    createPayor: createPayor(),
    updatePayor: updatePayor(),
    getPayorById: getPayorById(),
    createContract: createContract(),
    updateContract: updateContract(),
    deleteContract: deleteContract(),
    createServiceCodeLabel: createServiceCodeLabel(),
    updateServiceCodeLabel: updateServiceCodeLabel(),
  };

  function getList() {
    return createAsyncThunk(`${name}/getList`, async ({ getAccessTokenSilently }: any) => {
      const response = await fetchWrapper.get({ url: `${baseUrl}/`, getAccessTokenSilently });
      return response;
    });
  }

  function createPayor() {
    return createAsyncThunk(
      `${name}/createPayor`,
      async ({ data, getAccessTokenSilently }: any) => {
        const response = await fetchWrapper.post(
          { url: `${baseUrl}/`, getAccessTokenSilently },
          data,
        );
        return response;
      },
    );
  }
  function updatePayor() {
    return createAsyncThunk(
      `${name}/updatePayor`,
      async ({ data, getAccessTokenSilently, payorId }: any) => {
        const response = await fetchWrapper.put(
          { url: `${baseUrl}/${payorId}`, getAccessTokenSilently },
          data,
        );
        return response;
      },
    );
  }
  function getPayorById() {
    return createAsyncThunk(
      `${name}/getPayorById`,
      async ({ getAccessTokenSilently, payorId }: any) => {
        const response = await fetchWrapper.get({
          url: `${baseUrl}/${payorId}`,
          getAccessTokenSilently,
        });
        return response;
      },
    );
  }

  function createContract() {
    return createAsyncThunk(
      `${name}/createContract`,
      async ({ data, getAccessTokenSilently, payorId }: any) => {
        const response = await fetchWrapper.post(
          { url: `${baseUrl}/${payorId}/contracts/`, getAccessTokenSilently },
          data,
        );
        return response;
      },
    );
  }

  function updateContract() {
    return createAsyncThunk(
      `${name}/updateContract`,
      async ({ data, getAccessTokenSilently, payorId, contractId }: any) => {
        const response = await fetchWrapper.patch(
          { url: `${baseUrl}/${payorId}/contracts/${contractId}`, getAccessTokenSilently },
          data,
        );
        return response;
      },
    );
  }

  function deleteContract() {
    return createAsyncThunk(
      `${name}/deleteContract`,
      async ({ getAccessTokenSilently, payorId, contractId }: any) => {
        const response = await fetchWrapper.delete({
          url: `${baseUrl}/${payorId}/contracts/${contractId}`,
          getAccessTokenSilently,
        });
        return response;
      },
    );
  }

  function createServiceCodeLabel() {
    return createAsyncThunk(
      `${name}/createServiceCodeLabel`,
      async ({ data, getAccessTokenSilently, payorId }: any) => {
        const response = await fetchWrapper.post(
          { url: `${baseUrl}/${payorId}/service_code_descriptions/`, getAccessTokenSilently },
          data,
        );
        return response;
      },
    );
  }

  function updateServiceCodeLabel() {
    return createAsyncThunk(
      `${name}/updateServiceCodeLabel`,
      async ({ data, getAccessTokenSilently, payorId, serviceCodeDescriptionId }: any) => {
        const response = await fetchWrapper.patch(
          {
            url: `${baseUrl}/${payorId}/service_code_descriptions/${serviceCodeDescriptionId}/`,
            getAccessTokenSilently,
          },
          data,
        );
        return response;
      },
    );
  }
}

function createExtraReducers(builder: ActionReducerMapBuilder<PayorState>) {
  const {
    getList,
    createPayor,
    updatePayor,
    getPayorById,
    createContract,
    updateContract,
    deleteContract,
    createServiceCodeLabel,
    updateServiceCodeLabel,
  } = extraActions;

  builder
    // getList()
    .addCase(getList.pending, (state: PayorState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(getList.fulfilled, (state: PayorState, action: PayloadAction<IPayor[]>) => {
      state.list = action.payload;
      state.loading = false;
    })
    .addCase(getList.rejected, (state: PayorState, action: any) => {
      state.list = [];
      state.error = action.error;
      state.loading = false;
    })

    // createPayor()
    .addCase(createPayor.pending, (state: PayorState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(createPayor.fulfilled, (state: PayorState, _action: PayloadAction<IPayor>) => {
      state.loading = false;
      toast.success('New Payor Created');
    })
    .addCase(createPayor.rejected, (state: PayorState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(state.error.message);
    })

    //updatePayor()
    .addCase(updatePayor.pending, (state: PayorState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(updatePayor.fulfilled, (state: PayorState, _action: PayloadAction<IPayor>) => {
      state.loading = false;
      toast.success('Payor updated');
    })
    .addCase(updatePayor.rejected, (state: PayorState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(state.error.message);
    })

    //getPayorById()
    .addCase(getPayorById.pending, (state: PayorState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(getPayorById.fulfilled, (state: PayorState, action: PayloadAction<IPayor>) => {
      state.current = action.payload;
      state.loading = false;
    })
    .addCase(getPayorById.rejected, (state: PayorState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(state.error.message);
    })

    //createContract()
    .addCase(createContract.pending, (state: PayorState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(createContract.fulfilled, (state: PayorState, _action: PayloadAction<IPayor>) => {
      state.loading = false;
      toast.success('New contract created successfully', {
        toastId: 'payor-contract-create-success',
      });
    })
    .addCase(createContract.rejected, (state: PayorState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(state.error.message, {
        toastId: 'payor-contract-create-error',
      });
    })

    //updateContract()
    .addCase(updateContract.pending, (state: PayorState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(updateContract.fulfilled, (state: PayorState, _action: PayloadAction<IPayor>) => {
      state.loading = false;
      toast.success('Contract updated successfully', {
        toastId: 'payor-contract-update-success',
      });
    })
    .addCase(updateContract.rejected, (state: PayorState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(state.error.message, {
        toastId: 'payor-contract-update-error',
      });
    })

    //deleteContract()
    .addCase(deleteContract.pending, (state: PayorState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(deleteContract.fulfilled, (state: PayorState, _action: PayloadAction<IPayor>) => {
      state.loading = false;
      toast.success('Contract deleted successfully', {
        toastId: 'payor-contract-delete-success',
      });
    })
    .addCase(deleteContract.rejected, (state: PayorState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(state.error.message, {
        toastId: 'payor-contract-delete-error',
      });
    })

    //createServiceCodeLabel()
    .addCase(createServiceCodeLabel.pending, (state: PayorState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(
      createServiceCodeLabel.fulfilled,
      (state: PayorState, action: PayloadAction<IServiceCodeDescription>) => {
        state.loading = false;
        if (state.current) {
          state.current.service_code_descriptions = state.current.service_code_descriptions.map(
            (description) =>
              description.service_code_id === action.payload.service_code_id
                ? action.payload
                : description,
          );
        }
        toast.success('Service code label created', {
          toastId: 'payor-service-code-label-create-success',
        });
      },
    )
    .addCase(createServiceCodeLabel.rejected, (state: PayorState, action: any) => {
      state.error = action.error;
      state.loading = false;
    })

    //updateServiceCodeLabel()
    .addCase(updateServiceCodeLabel.pending, (state: PayorState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(
      updateServiceCodeLabel.fulfilled,
      (state: PayorState, action: PayloadAction<IServiceCodeDescription>) => {
        state.loading = false;
        //update the current payor with the new service code label where service_code_id matches
        if (state.current) {
          state.current.service_code_descriptions = state.current.service_code_descriptions.map(
            (serviceCodeDescription) =>
              serviceCodeDescription.id === action.payload.id
                ? action.payload
                : serviceCodeDescription,
          );
          toast.success('Service code label updated', {
            toastId: 'payor-service-code-label-update-success',
          });
        }
      },
    )
    .addCase(updateServiceCodeLabel.rejected, (state: PayorState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(state.error.message, {
        toastId: 'payor-service-code-label-update-error',
      });
    });
}

export const payorActions = { ...slice.actions, ...extraActions };
export const payorReducer = slice.reducer;
